import type { CreatedCanvas } from '../types';

/**
 * A cross-pane request to open the panel straight into a read-only mode.
 *
 * One tagged union rather than a prop per intent: the two are mutually
 * exclusive, exactly as `EditorMode` already encodes, so the impossible pair
 * cannot be set at once. `versionId` is named apart from the mode's `version`
 * (a resolved `CanvasVersion`) because this side carries only the number.
 */
export type PendingMode = { canvasId: string } & (
  | { kind: 'diffing'; from: number; to: number }
  | { kind: 'viewing-version'; versionId: number }
);

/**
 * What the panel is doing with a read-only view, reported back so chat cards can
 * label their buttons.
 *
 * A superset of `PendingMode`: fetching a version is observable, so a card can
 * spin while its own version loads. `loading-version` is report-only — it is
 * never a valid intent, which is why the two types stay distinct.
 */
export type ViewState = PendingMode | { canvasId: string; kind: 'loading-version'; versionId: number };

/**
 * Why the canvas is read-only while the assistant produces a turn, or null when it is not.
 * `editing` means the assistant is changing this canvas right now.
 */
export type TurnLock = 'responding' | 'editing' | null;

/**
 * The document the editor panel edits: a Pending Canvas, which has no server
 * state until its first save, or a saved canvas.
 *
 * A Pending Canvas has no session only on the new-chat screen, where its first
 * save creates one.
 */
export type EditorTarget =
  | { kind: 'pending'; sessionId?: string }
  | { kind: 'saved'; sessionId: string; canvasId: string };

export type PendingEditorTarget = Extract<EditorTarget, { kind: 'pending' }>;
export type SavedEditorTarget = Extract<EditorTarget, { kind: 'saved' }>;

/** A document to edit, with the title a Pending Canvas's always comes with. */
type WithPendingTitle<Title> =
  | { readonly target: SavedEditorTarget; readonly pendingTitle?: never }
  | { readonly target: PendingEditorTarget; readonly pendingTitle: Title };

/**
 * One editor panel kept mounted in the editor pane. A Pending Canvas's carries
 * the one copy of its title until the first save sends it.
 *
 * `key` identifies the mounted editor, not the document: a Pending Canvas keeps
 * its key when its first save makes it a Canvas, so its editor is not remounted.
 */
export type MountedPanel = { readonly key: string } & WithPendingTitle<string>;

/** A tab in the editor pane's tab bar: a Canvas of the chat, or one of its Pending Canvases. */
export type CanvasTab =
  | {
      readonly kind: 'saved';
      readonly canvasId: string;
      /** Undefined until the chat's canvas list includes it. */
      readonly title: string | undefined;
    }
  | {
      readonly kind: 'pending';
      /** The Pending Canvas's own key, as its editor's is. */
      readonly key: string;
      readonly title: string;
    };

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** Which tab the panel shows. */
export type ShownTab = DistributiveOmit<CanvasTab, 'title'>;

/** Starts every Pending Canvas's key. Canvas ids are ULIDs, so a key can never collide with one. */
export const PENDING_KEY_PREFIX = 'pending-';

/** Return whether `key` names a Pending Canvas rather than a canvas id. */
export const isPendingKey = (key: string): boolean => key.startsWith(PENDING_KEY_PREFIX);

/** Return the key naming a tab: its canvas id, or the Pending Canvas's key. */
export const tabKey = (tab: ShownTab): string => (tab.kind === 'saved' ? tab.canvasId : tab.key);

/** Return the tab among `tabs` whose key is `key`, if any. */
export const findTab = <Tab extends ShownTab>(tabs: readonly Tab[], key: string): Tab | undefined =>
  tabs.find((tab) => tabKey(tab) === key);

/** Return whether `shown`, the panel's tab or null while it is closed, is the tab with `key`. */
export const isShownTab = (shown: ShownTab | null, key: string): boolean => shown !== null && tabKey(shown) === key;

/** A Pending Canvas's title, held by the owner of the tabs until the first save sends it. */
export interface PendingTitle {
  readonly value: string;
  readonly onChange: (title: string) => void;
}

/** The document an editor panel edits: a Pending Canvas's comes with its title. */
export type EditorPanelDocument = WithPendingTitle<PendingTitle>;

/** What the chat reaches into a mounted document for, outside the controls it renders. */
export interface CanvasDocumentHandle {
  /**
   * Save the live buffer if it has unsaved edits, or a Pending Canvas even without any.
   *
   * Resolves with the created canvas when a Pending Canvas was saved, else null;
   * the caller completes the switch to it.
   */
  save: () => Promise<CreatedCanvas | null>;
  isDirty: () => boolean;
  /** Drop unsaved edits, back to the latest saved content: empty for a Pending Canvas. */
  discard: () => void;
  /** Return the version shown read-only, or null while live or showing changes. */
  getViewedVersionId: () => number | null;
  /** Leave a read-only mode back to the live editor. No-op if already live. */
  exitReadOnly: () => void;
}
